/**
 * When the booking wave goes out — presets, with a per-day grid behind
 * Custom.
 *
 * One component for both surfaces (organisation settings and a
 * campaign's settings tab), because the schedule is the same shape and
 * only the inheritance badge differs.
 *
 * Presets first, grid behind Custom, because that is where a per-day grid
 * belongs: the common answer is one click, and the grid is the escape
 * hatch rather than the default view. The grid itself is self-describing —
 * what happens on each day is visible without combining two controls.
 *
 * The timezone and the next run are printed from the SERVER's values. They
 * depend on DST and on a zone the browser may not be in; a client
 * computing either would reintroduce the exact bug this replaces, where
 * a page said "at 10:00" and meant 10:00 UTC.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";

import {
  DAY_KEYS,
  type DayKey,
  type Days,
  type Preset,
  type WaveScheduleResponse,
  applyPreset,
  dominantTime,
  isPaused,
  presetFor,
  setDayTime,
  timeProblems,
  toPayload,
  toggleDay,
} from "./waveSchedule";

export type WaveScope = { kind: "org" } | { kind: "campaign"; id: string };

const PRESETS: Preset[] = ["weekdays", "daily", "custom"];

export function WaveScheduleEditor({ scope }: { scope: WaveScope }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const url =
    scope.kind === "org"
      ? ApiPaths.orgWaveSchedule
      : ApiPaths.campaignWaveSchedule(scope.id);
  const key = [
    "wave-schedule",
    scope.kind,
    scope.kind === "campaign" ? scope.id : "me",
  ];

  const loaded = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<WaveScheduleResponse>(url)).data,
  });

  const [days, setDays] = useState<Days | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Held separately: a schedule whose days all share a time is a preset,
  // but the operator may have OPENED Custom without changing anything
  // yet, and snapping them back to "Weekdays" mid-edit would be rude.
  const [forcedCustom, setForcedCustom] = useState(false);

  // Re-seed only while clean, so a background refetch cannot discard what
  // has been typed.
  useEffect(() => {
    if (loaded.data && !dirty) setDays(loaded.data.schedule.days);
  }, [loaded.data, dirty]);

  const save = useMutation({
    mutationFn: async (next: Days) =>
      (await api.put<WaveScheduleResponse>(url, toPayload(next))).data,
    onSuccess: (data) => {
      setDays(data.schedule.days);
      setDirty(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail ?? t("wave.saveFailed"));
    },
  });

  const reset = useMutation({
    mutationFn: async () => (await api.delete<WaveScheduleResponse>(url)).data,
    onSuccess: (data) => {
      setDays(data.schedule.days);
      setDirty(false);
      setForcedCustom(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  if (loaded.isLoading || !days || !loaded.data) {
    return <p className="text-sm text-nb-text-3">{t("common.loading")}</p>;
  }

  const meta = loaded.data;
  const current: Days = days;
  const preset: Preset = forcedCustom ? "custom" : presetFor(current);
  const problems = timeProblems(current);
  const paused = isPaused(current);

  function edit(next: Days) {
    setDays(next);
    setDirty(true);
    setError(null);
  }

  function choosePreset(next: Preset) {
    setForcedCustom(next === "custom");
    if (next !== "custom") edit(applyPreset(next, current));
    else setDirty(true);
  }

  const fmtNext = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: meta.timezone,
    });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-base font-semibold text-nb-text">
          {t("wave.title")}
        </h3>
        <p className="mt-1 max-w-prose text-sm text-nb-text-2">
          {t("wave.intro")}
        </p>
      </div>

      {/* The zone is named, always. A page that says "10:00" without
          saying whose was half of the original bug. */}
      <p className="text-xs text-nb-text-2" data-testid="wave-timezone">
        {t(`wave.tzSource.${meta.timezone_source}`, {
          zone: meta.timezone,
        })}{" "}
        <span className="text-nb-text-3">
          {t("wave.tzDst")}
        </span>
      </p>

      {scope.kind === "campaign" && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className="rounded-full border border-slate-600 px-2 py-0.5 font-mono text-nb-text-3"
            data-testid="wave-inheritance"
          >
            {meta.inherited ? t("wave.inherited") : t("wave.ownSchedule")}
          </span>
          {!meta.inherited && (
            <button
              type="button"
              className="text-nb-mint hover:underline"
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              data-testid="wave-reset"
            >
              {t("wave.resetToOrg")}
            </button>
          )}
        </div>
      )}

      {/* ---- presets ---- */}
      <div className="space-y-1.5">
        <span className="font-mono text-[0.65rem] uppercase tracking-wider text-nb-text-3">
          {t("wave.days")}
        </span>
        <div
          className="inline-flex overflow-hidden rounded-md border border-slate-700"
          role="group"
          aria-label={t("wave.days")}
        >
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              className={`px-3 py-1 text-xs ${
                preset === p
                  ? "bg-nb-mint/15 text-nb-mint"
                  : "text-nb-text-3 hover:text-nb-text-2"
              } border-r border-slate-700 last:border-r-0`}
              onClick={() => choosePreset(p)}
              data-testid={`wave-preset-${p}`}
            >
              {t(`wave.preset.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {/* ---- one time, for the presets ---- */}
      {preset !== "custom" && (
        <label className="block space-y-1.5">
          <span className="font-mono text-[0.65rem] uppercase tracking-wider text-nb-text-3">
            {t("wave.sendAt")}
          </span>
          <input
            type="time"
            className="input w-32 py-1 text-sm"
            value={dominantTime(current)}
            onChange={(e) => {
              const time = e.target.value;
              edit(
                Object.fromEntries(
                  DAY_KEYS.filter((d) => current[d]).map((d) => [d, time]),
                ) as Days,
              );
            }}
            data-testid="wave-single-time"
          />
        </label>
      )}

      {/* ---- the grid, behind Custom ---- */}
      {preset === "custom" && (
        <div className="overflow-x-auto rounded-md border border-slate-700">
          <table className="w-full min-w-[22rem] text-sm">
            <thead>
              <tr className="text-left text-[0.65rem] uppercase tracking-wider text-nb-text-3">
                <th className="px-3 py-2 font-medium">{t("wave.colDay")}</th>
                <th className="w-16 px-3 py-2 text-center font-medium">
                  {t("wave.colSend")}
                </th>
                <th className="px-3 py-2 font-medium">{t("wave.colAt")}</th>
              </tr>
            </thead>
            <tbody>
              {DAY_KEYS.map((d) => {
                const on = Boolean(current[d]);
                return (
                  <tr key={d} className="border-t border-slate-700">
                    <td className="px-3 py-2 text-nb-text">
                      {t(`wave.day.${d}`)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${t(`wave.day.${d}`)} — ${t("wave.colSend")}`}
                        checked={on}
                        onChange={(e) =>
                          edit(toggleDay(current, d as DayKey, e.target.checked))
                        }
                        data-testid={`wave-day-${d}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {on ? (
                        <input
                          type="time"
                          className="input w-28 py-1 text-sm"
                          aria-label={`${t(`wave.day.${d}`)} — ${t("wave.colAt")}`}
                          value={current[d] ?? ""}
                          onChange={(e) =>
                            edit(setDayTime(current, d as DayKey, e.target.value))
                          }
                          data-testid={`wave-time-${d}`}
                        />
                      ) : (
                        <span className="text-xs text-nb-text-3">
                          {t("wave.noWave")}
                        </span>
                      )}
                      {problems.has(d as DayKey) && (
                        <p className="mt-1 text-[0.7rem] text-cvp-danger">
                          {t("wave.badTime")}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- what happens next ---- */}
      <div
        className="rounded-md border border-slate-700 bg-nb-surface-2 px-3 py-2 text-xs text-nb-text-2"
        data-testid="wave-next"
      >
        {paused ? (
          <span className="text-amber-300">{t("wave.pausedNote")}</span>
        ) : meta.next_run ? (
          <>
            {t("wave.nextRun", { when: fmtNext(meta.next_run) })}
            {dirty && (
              <span className="ml-1 text-nb-text-3">{t("wave.saveToApply")}</span>
            )}
          </>
        ) : (
          t("wave.noNextRun")
        )}
      </div>

      <p className="max-w-prose text-[0.7rem] leading-snug text-nb-text-3">
        {t("wave.lateNote", {
          hours: meta.late_grace_hours,
          hour: meta.latest_local_hour,
        })}
      </p>

      {error && <p className="text-sm text-cvp-danger">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => save.mutate(current)}
          disabled={!dirty || problems.size > 0 || save.isPending}
          data-testid="wave-save"
        >
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
        {problems.size > 0 && (
          <span className="text-xs text-cvp-danger">{t("wave.fixTimes")}</span>
        )}
      </div>
    </div>
  );
}
